import { useMemo, useState } from 'react';
import { useDaten } from '../daten/kontext';
import type { Id, Dozent, Datum, Abwesenheit } from '../daten/typen';
import { knotenPfad, fachbereichsKnoten } from '../daten/regeln';
import { plusTage, wochentag, deLang, kurzeZeit, monatName, heute, zeitpunkt, ueberschneidet, tagKurz } from '../daten/zeit';
import { Knopf, Kuerzel, Feld, Icon, Seitenpanel, dozentName, Leer } from '../ui/bausteine';
import { dozentTerminlisteDrucken, dozentKalender, herunterladen } from '../daten/export';

export function Dozenten() {
  const { stand, quelle, ausfuehren } = useDaten();
  const [suche, setSuche] = useState('');
  const [auswahl, setAuswahl] = useState<Id | null>(null);
  const [neu, setNeu] = useState(false);
  const liste = stand.dozenten
    .filter(d => !suche || `${d.vorname} ${d.nachname} ${d.kuerzel}`.toLowerCase().includes(suche.toLowerCase()))
    .sort((a, b) => Number(b.aktiv) - Number(a.aktiv) || a.nachname.localeCompare(b.nachname, 'de'));
  const d = stand.dozenten.find(x => x.id === auswahl);
  const einladen = (id: Id) => {
    const x = stand.dozenten.find(y => y.id === id);
    return ausfuehren(() => quelle.dozentEinladen(id), `Einladung an ${x?.email ?? 'den Dozenten'} verschickt`);
  };

  return (
    <div className="seite">
      <header className="seiten-kopf zeile">
        <div><h1>Dozenten</h1><p className="leise">{stand.dozenten.filter(x => x.aktiv).length} aktiv · Fachbereiche, Abwesenheiten und Stunden</p></div>
        <div className="zeile">
          <input type="search" placeholder="Suchen …" value={suche} onChange={e => setSuche(e.target.value)} aria-label="Dozenten suchen" />
          <Knopf art="primaer" icon="plus" onClick={() => setNeu(true)}>Dozent anlegen</Knopf>
        </div>
      </header>
      <div className="tabelle-rahmen">
        <table className="tabelle">
          <thead><tr><th>Dozent</th><th>Beschäftigung</th>{stand.fachbereiche.map(f => <th key={f.id} title={f.bezeichnung}>{f.kuerzel}</th>)}<th>App</th><th>Abwesenheiten</th><th>UE geplant (lfd. Monat)</th></tr></thead>
          <tbody>
            {liste.map(x => {
              const monat = heute().slice(0, 7);
              const ue = stand.einsaetze.filter(e => e.dozent_id === x.id && e.status !== 'entfaellt' && e.datum.startsWith(monat)).reduce((s, e) => s + e.ue, 0);
              const abw = stand.abwesenheiten.filter(a => a.dozent_id === x.id && a.bis >= `${heute()} 00:00` && a.quelle !== 'absage').length;
              return (
                <tr key={x.id} className={x.aktiv ? '' : 'inaktiv'}>
                  <td><button type="button" className="link zeile" onClick={() => setAuswahl(x.id)}><Kuerzel text={x.kuerzel} farbe={x.farbe} klein /> {dozentName(x)}</button></td>
                  <td>{x.beschaeftigung === 'honorar' ? 'Honorar' : 'angestellt'}</td>
                  {stand.fachbereiche.map(f => {
                    const an = stand.qualifikationen.some(q => q.dozent_id === x.id && q.fachbereich_id === f.id);
                    return <td key={f.id}><button type="button" className={`haken-knopf ${an ? 'an' : ''}`} aria-pressed={an} aria-label={`${f.bezeichnung} ${an ? 'entfernen' : 'zuordnen'}`}
                      style={{ ['--fbfarbe' as any]: f.farbe }} onClick={() => ausfuehren(() => quelle.qualifikationSetzen(x.id, f.id, !an))}>{an ? <Icon name="haken" groesse={14} /> : ''}</button></td>;
                  })}
                  <td>{stand.appNutzer.includes(x.id) ? <span className="tag status-aktiv">Zugang</span>
                    : <button type="button" className="link" onClick={() => einladen(x.id)}>Einladen</button>}</td>
                  <td>{abw > 0 ? `${abw} künftige` : '–'}</td>
                  <td>{ue}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <Seitenpanel offen={!!d} schliessen={() => setAuswahl(null)} titel={d ? dozentName(d) : ''} untertitel={d ? `${d.kuerzel} · ${d.beschaeftigung === 'honorar' ? 'Honorardozent' : 'angestellt'}` : ''}>
        {d && <DozentDetail key={d.id} d={d} />}
      </Seitenpanel>
      <Seitenpanel offen={neu} schliessen={() => setNeu(false)} titel="Dozent anlegen">
        {neu && <DozentFormular schliessen={() => setNeu(false)} />}
      </Seitenpanel>
    </div>
  );
}

function DozentFormular({ d, schliessen }: { d?: Dozent; schliessen: () => void }) {
  const { quelle, ausfuehren } = useDaten();
  const [w, setW] = useState<Partial<Dozent>>(d ?? { anrede: '', vorname: '', nachname: '', kuerzel: '', email: '', beschaeftigung: 'honorar', farbe: '#073070', aktiv: true });
  const set = (k: keyof Dozent, v: any) => setW({ ...w, [k]: v });
  return (
    <form className="formular" onSubmit={async e => { e.preventDefault();
      if (await ausfuehren(() => quelle.speichern('dozent', { ...w, kuerzel: (w.kuerzel ?? '').toUpperCase() }), 'Dozent gespeichert')) schliessen(); }}>
      <div className="raster-2">
        <Feld label="Anrede / Titel"><input value={w.anrede ?? ''} onChange={e => set('anrede', e.target.value)} placeholder="Fr. / Hr. / Dr." /></Feld>
        <Feld label="Kürzel"><input required maxLength={4} value={w.kuerzel ?? ''} onChange={e => set('kuerzel', e.target.value)} /></Feld>
        <Feld label="Vorname"><input required value={w.vorname ?? ''} onChange={e => set('vorname', e.target.value)} /></Feld>
        <Feld label="Nachname"><input required value={w.nachname ?? ''} onChange={e => set('nachname', e.target.value)} /></Feld>
      </div>
      <Feld label="E-Mail" hinweis="An diese Adresse geht später der Zugang zur Dozenten-App."><input type="email" value={w.email ?? ''} onChange={e => set('email', e.target.value)} /></Feld>
      <div className="raster-2">
        <Feld label="Beschäftigung"><select value={w.beschaeftigung} onChange={e => set('beschaeftigung', e.target.value)}><option value="angestellt">angestellt</option><option value="honorar">Honorar</option></select></Feld>
        <Feld label="Farbe"><input type="color" value={w.farbe} onChange={e => set('farbe', e.target.value)} /></Feld>
      </div>
      <label className="check"><input type="checkbox" checked={!!w.aktiv} onChange={e => set('aktiv', e.target.checked)} /> aktiv (kann eingeplant werden)</label>
      <div className="knopfleiste"><Knopf art="primaer" icon="haken" type="submit">Speichern</Knopf></div>
    </form>
  );
}

function DozentDetail({ d }: { d: Dozent }) {
  const [reiter, setReiter] = useState<'abwesend' | 'einsaetze' | 'stamm'>('abwesend');
  const { stand, quelle, ausfuehren } = useDaten();
  const hatApp = stand.appNutzer.includes(d.id);
  return (
    <div>
      <div className={`hinweisbox ${hatApp ? 'info' : 'warn'} app-status`}>
        <Icon name={hatApp ? 'haken' : 'person'} />
        <span>{hatApp ? 'Nutzt die Dozenten-App: Abwesenheiten trägt der Dozent selbst ein und sieht seine Einsätze live.'
          : 'Hat noch keinen Zugang zur Dozenten-App. Abwesenheiten bitte hier eintragen (z. B. aus dem Word-Formular).'}</span>
        {!hatApp && <Knopf art="primaer" icon="senden" disabled={!d.email}
          title={d.email ? `Einladung an ${d.email}` : 'Bitte zuerst unter „Stammdaten“ eine E-Mail-Adresse eintragen'}
          onClick={() => ausfuehren(() => quelle.dozentEinladen(d.id), `Einladung an ${d.email} verschickt`)}>Zur App einladen</Knopf>}
      </div>
      <div className="reiter" role="tablist">
        <button role="tab" aria-selected={reiter === 'abwesend'} className={reiter === 'abwesend' ? 'an' : ''} onClick={() => setReiter('abwesend')}>Abwesenheiten</button>
        <button role="tab" aria-selected={reiter === 'einsaetze'} className={reiter === 'einsaetze' ? 'an' : ''} onClick={() => setReiter('einsaetze')}>Einsätze und Stunden</button>
        <button role="tab" aria-selected={reiter === 'stamm'} className={reiter === 'stamm' ? 'an' : ''} onClick={() => setReiter('stamm')}>Stammdaten</button>
      </div>
      {reiter === 'abwesend' && <AbwesenheitsKalender dozentId={d.id} quelleArt="admin" />}
      {reiter === 'einsaetze' && <Stunden dozentId={d.id} />}
      {reiter === 'stamm' && <DozentFormular d={d} schliessen={() => setReiter('abwesend')} />}
    </div>
  );
}

/** Lesbarer Zeitraum einer Abwesenheit, z. B. „06.10.2026, 08:00–11:30“ oder „12.10. – 16.10.2026“ */
export function zeitraumText(von: string, bis: string): string {
  const vd = von.slice(0, 10), vz = von.slice(11, 16);
  const ganzTage = vz === '00:00' && bis.endsWith('00:00');
  const bd = ganzTage ? plusTage(bis.slice(0, 10), -1) : bis.slice(0, 10);
  const bz = bis.slice(11, 16) === '00:00' ? '24:00' : bis.slice(11, 16);
  if (ganzTage) return vd === bd ? `${deLang(vd)} (ganztägig)` : `${deLang(vd).slice(0, 6)} – ${deLang(bd)}`;
  if (vd === bd) return `${deLang(vd)}, ${vz}–${bz}`;
  return `${deLang(vd)} ${vz} – ${deLang(bd)} ${bz}`;
}

type Teil = 'ganz' | 'vm' | 'nm';
const TEIL_ZEIT: Record<Teil, [string, string]> = { ganz: ['00:00', '24:00'], vm: ['00:00', '12:00'], nm: ['12:00', '24:00'] };
const bisZeit = (datum: Datum, t: string) => t === '24:00' ? `${plusTage(datum, 1)} 00:00` : `${datum} ${t}`;

/** Monatskalender zum Eintragen, wann ein Dozent NICHT kann. Wird auch in der Dozenten-App genutzt. */
export function AbwesenheitsKalender({ dozentId, quelleArt }: { dozentId: Id; quelleArt: 'admin' | 'dozent' }) {
  const { stand, quelle, ausfuehren, fragen, melden } = useDaten();
  const [monat, setMonat] = useState(heute().slice(0, 7));
  const [pinsel, setPinsel] = useState<Teil>('ganz');
  const [grund, setGrund] = useState('');
  const abw = stand.abwesenheiten.filter(a => a.dozent_id === dozentId);
  const [j, m] = monat.split('-').map(Number);
  const erster = `${monat}-01`;
  const start = plusTage(erster, -wochentag(erster));
  const tage = useMemo(() => Array.from({ length: 42 }, (_, i) => plusTage(start, i)), [start]);

  const treffer = (datum: Datum, teil: Teil): Abwesenheit | undefined => {
    const [b, e] = TEIL_ZEIT[teil];
    return abw.find(a => ueberschneidet(a.von, a.bis, `${datum} ${b}`, bisZeit(datum, e)));
  };
  const einsatzAm = (datum: Datum) => stand.einsaetze.some(e => e.dozent_id === dozentId && e.datum === datum && e.status !== 'entfaellt');

  async function tippen(datum: Datum) {
    const vorhanden = treffer(datum, pinsel);
    if (vorhanden) {
      const mehrtaegig = vorhanden.bis > `${plusTage(datum, 1)} 00:00` || vorhanden.von < `${datum} 00:00`;
      if (mehrtaegig && !await fragen(`Diese Abwesenheit reicht von ${deLang(vorhanden.von.slice(0, 10))} bis ${deLang(plusTage(vorhanden.bis.slice(0, 10), vorhanden.bis.endsWith('00:00') ? -1 : 0))}. Ganz entfernen?`, 'Entfernen', true)) return;
      if (vorhanden.quelle === 'absage') { melden('Eine Absage kann hier nicht entfernt werden.', 'info'); return; }
      await ausfuehren(() => quelle.abwesenheitLoeschen(vorhanden.id), 'Abwesenheit entfernt');
      return;
    }
    const [b, e] = TEIL_ZEIT[pinsel];
    const konflikt = stand.einsaetze.filter(x => x.dozent_id === dozentId && x.status !== 'entfaellt'
      && ueberschneidet(`${datum} ${b}`, bisZeit(datum, e), zeitpunkt(x.datum, x.beginn), zeitpunkt(x.datum, x.ende)));
    if (konflikt.length && !await fragen(`An diesem Tag ist bereits ${konflikt.length === 1 ? 'ein Einsatz' : konflikt.length + ' Einsätze'} geplant. Die Abwesenheit gilt dann als Absage, und der Termin wird zur Vertretung freigegeben. Fortfahren?`, 'Eintragen')) return;
    await ausfuehren(() => quelle.abwesenheitSpeichern({ dozent_id: dozentId, von: `${datum} ${b}`, bis: bisZeit(datum, e), grund: grund.trim() || null, quelle: quelleArt }),
      konflikt.length ? 'Abwesenheit eingetragen – Termin als Absage gemeldet' : 'Abwesenheit eingetragen');
  }

  const kuenftig = abw.filter(a => a.bis >= `${heute()} 00:00`).sort((a, b) => a.von.localeCompare(b.von));
  return (
    <div className="abw">
      <p className="leise klein">{quelleArt === 'admin' ? 'Für Dozenten, die die App nicht nutzen: Rückmeldung (z. B. aus dem Word-Formular) hier eintragen. ' : ''}Tippen Sie auf einen Tag, um ihn als „nicht verfügbar“ zu markieren; erneutes Tippen entfernt die Markierung.</p>
      <div className="zeile umbruch">
        <div className="segment">
          {(['ganz', 'vm', 'nm'] as Teil[]).map(t => <button key={t} type="button" className={pinsel === t ? 'an' : ''} onClick={() => setPinsel(t)}>{t === 'ganz' ? 'Ganzer Tag' : t === 'vm' ? 'Vormittag' : 'Nachmittag/Abend'}</button>)}
        </div>
        <input type="text" placeholder="Grund (optional, z. B. Urlaub)" value={grund} onChange={e => setGrund(e.target.value)} aria-label="Grund" />
      </div>
      <div className="monat-kopf">
        <Knopf art="leise" icon="links" aria-label="Voriger Monat" onClick={() => { const x = new Date(j, m - 2, 1); setMonat(`${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}`); }} />
        <strong>{monatName(m - 1)} {j}</strong>
        <Knopf art="leise" icon="rechts" aria-label="Nächster Monat" onClick={() => { const x = new Date(j, m, 1); setMonat(`${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}`); }} />
      </div>
      <div className="monat">
        {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map(t => <div key={t} className="monat-wt">{t}</div>)}
        {tage.map(t => {
          const vm = treffer(t, 'vm'), nm = treffer(t, 'nm');
          const fremd = !t.startsWith(monat);
          const ft = stand.kalender.find(k => k.datum === t && !k.standort_id);
          return (
            <button key={t} type="button" className={`monat-tag ${fremd ? 'fremd' : ''} ${t === heute() ? 'heute' : ''} ${ft ? 'feiertag' : ''}`}
              onClick={() => tippen(t)} aria-label={`${tagKurz(t)} ${deLang(t)}${vm || nm ? ', nicht verfügbar' : ''}`} title={ft?.bezeichnung ?? vm?.grund ?? nm?.grund ?? ''}>
              <span className="monat-zahl">{Number(t.slice(8))}</span>
              <span className="monat-haelften">
                <span className={vm ? (vm.quelle === 'absage' ? 'abw absage' : 'abw') : ''} />
                <span className={nm ? (nm.quelle === 'absage' ? 'abw absage' : 'abw') : ''} />
              </span>
              {einsatzAm(t) && <span className="monat-punkt" title="Einsatz geplant" />}
            </button>
          );
        })}
      </div>
      <p className="leise klein legende"><span className="lg abw" /> nicht verfügbar <span className="lg absage" /> Absage <span className="lg punkt" /> Einsatz geplant</p>
      <h3 className="abschnitt">Eingetragene Abwesenheiten</h3>
      {kuenftig.length === 0 ? <p className="leise">Keine künftigen Abwesenheiten.</p> : (
        <ul className="liste-einfach">
          {kuenftig.map(a => (
            <li key={a.id} className="zeile">
              <span>{zeitraumText(a.von, a.bis)}</span>
              <span className="leise">{a.grund ?? ''}{a.quelle === 'absage' ? ' (Absage)' : a.quelle === 'admin' ? ' (Verwaltung)' : ''}</span>
              {a.quelle !== 'absage' && <Knopf art="leise" icon="muell" aria-label="Entfernen" onClick={() => ausfuehren(() => quelle.abwesenheitLoeschen(a.id), 'Abwesenheit entfernt')} />}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Einsätze und Stunden eines Dozenten mit Filter, CSV-, PDF- und Kalender-Export (auch für die Dozenten-App) */
export function Stunden({ dozentId }: { dozentId: Id }) {
  const { stand } = useDaten();
  const [von, setVon] = useState(`${heute().slice(0, 7)}-01`);
  const [bis, setBis] = useState(plusTage(`${heute().slice(0, 7)}-01`, 40).slice(0, 7) + '-01');
  const [kursId, setKursId] = useState<Id | ''>('');
  const [nurFrei, setNurFrei] = useState(true);
  const liste = stand.einsaetze.filter(e => e.dozent_id === dozentId && e.datum >= von && e.datum < bis && e.status !== 'entfaellt'
    && (!kursId || e.kurs_id === kursId) && (!nurFrei || e.status === 'freigegeben')).sort((a, b) => (a.datum + a.beginn).localeCompare(b.datum + b.beginn));
  const summe = liste.reduce((s, e) => s + e.ue, 0);
  const kurse = [...new Set(stand.einsaetze.filter(e => e.dozent_id === dozentId).map(e => e.kurs_id))].map(id => stand.kurse.find(k => k.id === id)!).filter(Boolean);
  const d = stand.dozenten.find(x => x.id === dozentId)!;
  const { melden, quelle } = useDaten();
  async function kopieren() {
    const tsv = tabelle().map(z => z.join('\t')).join('\n');
    try { await navigator.clipboard.writeText(tsv); melden('Tabelle kopiert – in Excel einfügen mit Strg+V'); }
    catch { melden('Kopieren wird von diesem Browser nicht erlaubt. Bitte „CSV für Excel“ nutzen.', 'info'); }
  }

  function tabelle() {
    const kopf = ['Datum', 'Wochentag', 'Beginn', 'Ende', 'UE', 'Kurs', 'Fachbereich', 'Thema', 'Form', 'Raum', 'Status'];
    const zeilen = liste.map(e => {
      const fbk = fachbereichsKnoten(stand, e.kurs_knoten_id);
      return [deLang(e.datum), tagKurz(e.datum), kurzeZeit(e.beginn), kurzeZeit(e.ende), String(e.ue), stand.kurse.find(k => k.id === e.kurs_id)?.kennung ?? '',
        stand.fachbereiche.find(f => f.id === fbk?.fachbereich_id)?.bezeichnung ?? '', knotenPfad(stand, e.kurs_knoten_id).map(k => k.bezeichnung).join(' > '),
        e.form === 'online' ? 'online' : 'Präsenz', stand.raeume.find(r => r.id === e.raum_id)?.bezeichnung ?? '', e.status];
    });
    zeilen.push(['Summe', '', '', '', String(summe), '', '', '', '', '', '']);
    return [kopf, ...zeilen];
  }
  function alleTermine() {
    const daten = stand.einsaetze.filter(e => e.dozent_id === dozentId && e.status !== 'entfaellt').map(e => e.datum).sort();
    if (!daten.length) return;
    setVon(daten[0]); setBis(plusTage(daten[daten.length - 1], 1));
  }
  function alsPdf() {
    const ok = dozentTerminlisteDrucken(stand, dozentId, liste, { von, bis, kursKennung: kurse.find(k => k.id === kursId)?.kennung ?? null, nurFreigegeben: nurFrei });
    if (!ok) melden('Die Druckansicht wurde vom Browser blockiert. Bitte Pop-ups für diese Seite erlauben.', 'info');
  }
  function alsKalender() {
    herunterladen(`Einsaetze_${d.kuerzel}_${von}_bis_${plusTage(bis, -1)}.ics`, dozentKalender(stand, liste), 'text/calendar;charset=utf-8');
  }
  function exportieren() {
    const csv = '﻿' + tabelle().map(z => z.map(f => `"${f.replace(/"/g, '""')}"`).join(';')).join('\r\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    a.download = `Einsaetze_${d.kuerzel}_${von}_bis_${plusTage(bis, -1)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
  }

  return (
    <div className="stunden">
      <div className="raster-2">
        <Feld label="Von"><input type="date" value={von} onChange={e => setVon(e.target.value)} /></Feld>
        <Feld label="Bis (ausschließlich)"><input type="date" value={bis} onChange={e => setBis(e.target.value)} /></Feld>
        <Feld label="Kurs"><select value={kursId} onChange={e => setKursId(e.target.value)}><option value="">alle Kurse</option>{kurse.map(k => <option key={k.id} value={k.id}>{k.kennung}</option>)}</select></Feld>
        <label className="check"><input type="checkbox" checked={nurFrei} onChange={e => setNurFrei(e.target.checked)} /> nur freigegebene Termine</label>
        <div><Knopf art="leise" icon="kalender" onClick={alleTermine}>Alle Termine</Knopf></div>
      </div>
      <div className="summe"><strong>{summe} UE</strong> in {liste.length} Einsätzen {quelle.art !== 'demo' && <Knopf icon="download" onClick={exportieren} disabled={!liste.length}>CSV für Excel</Knopf>}<Knopf art="leise" icon="liste" onClick={kopieren} disabled={!liste.length}>Tabelle kopieren</Knopf>
        <Knopf art="leise" icon="download" onClick={alsPdf} disabled={!liste.length}>PDF</Knopf>{quelle.art !== 'demo' && <Knopf art="leise" icon="kalender" onClick={alsKalender} disabled={!liste.length}>Kalender (.ics)</Knopf>}</div>
      {liste.length === 0 ? <Leer titel="Keine Einsätze im Zeitraum" /> : (
        <ul className="liste-einfach">
          {liste.map(e => <li key={e.id} className="zeile"><span>{tagKurz(e.datum)} {deLang(e.datum)}</span><span>{kurzeZeit(e.beginn)}–{kurzeZeit(e.ende)}</span>
            <span>{stand.kurse.find(k => k.id === e.kurs_id)?.kennung}</span><span className="leise">{knotenPfad(stand, e.kurs_knoten_id).slice(-1)[0]?.bezeichnung ?? ''}</span>
            {e.status === 'entwurf' && <span className="tag">Entwurf</span>}</li>)}
        </ul>
      )}
    </div>
  );
}
